import { useCallback, useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, Search, Plus, ExternalLink, Trash2, AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";

const humanize = (v) => (v ? String(v).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "—");

const STATUSES = ["draft", "published", "closed"];
const EMPLOYMENT_TYPES = ["full_time", "part_time", "per_diem", "contract", "temporary", "internship"];

export default function AdminJobs() {
  const { refreshOverview } = useOutletContext();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [q, setQ] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { include_archived: includeArchived };
      if (statusFilter) params.status_filter = statusFilter;
      if (typeFilter) params.employment_type = typeFilter;
      if (q.trim()) params.q = q.trim();
      const r = await api.get("/admin/jobs", { params });
      setJobs(r.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  }, [statusFilter, typeFilter, q, includeArchived]);

  useEffect(() => { load(); }, [load]);

  const patchStatus = async (id, status) => {
    try {
      await api.patch(`/admin/jobs/${id}`, { status });
      toast.success(`Marked ${humanize(status)}`);
      load();
      refreshOverview?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const toggleArchive = async (job) => {
    try {
      await api.patch(`/admin/jobs/${job.id}`, { archived: !job.archived });
      toast.success(job.archived ? "Unarchived" : "Archived");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/admin/jobs/${confirmDelete.id}`);
      toast.success("Job posting deleted");
      setConfirmDelete(null);
      load();
      refreshOverview?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div data-testid="admin-jobs">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <h2 className="font-heading text-2xl tracking-tight text-slate-900">Job Postings</h2>
          <p className="mt-1 text-sm text-slate-500">{jobs.length} posting{jobs.length === 1 ? "" : "s"}</p>
        </div>
        <div className="flex gap-2 self-start">
          <button onClick={load} data-testid="refresh-jobs" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:border-[#4A6FA5] hover:text-[#4A6FA5]"><RefreshCw className="w-4 h-4" /> Refresh</button>
          <Link to="/care-portal/admin/jobs/new" data-testid="new-job-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#4A6FA5] text-white text-sm font-medium hover:bg-[#3B597B]"><Plus className="w-4 h-4" /> New Job</Link>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, location, department…" data-testid="search-jobs" className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6FA5]" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="filter-job-status" className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{humanize(s)}</option>)}
        </select>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="filter-job-type" className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
          <option value="">All employment types</option>
          {EMPLOYMENT_TYPES.map((t) => <option key={t} value={t}>{humanize(t)}</option>)}
        </select>
        <label className="inline-flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} data-testid="include-archived" /> Show archived
        </label>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? <div className="p-12 text-center text-slate-500 text-sm">Loading…</div>
        : jobs.length === 0 ? <div className="p-12 text-center text-slate-500 text-sm" data-testid="empty-jobs">No job postings yet.</div>
        : (<div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-[0.14em] text-slate-500">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Title</th>
                <th className="text-left px-4 py-3 font-semibold">Location</th>
                <th className="text-left px-4 py-3 font-semibold">Type</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">Applications</th>
                <th className="text-left px-4 py-3 font-semibold">Updated</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {jobs.map((j) => (
                <tr key={j.id} className={`hover:bg-slate-50/60 ${j.archived ? "opacity-60" : ""}`} data-testid={`job-row-${j.slug}`}>
                  <td className="px-4 py-3 text-slate-900 font-medium align-top">
                    <div>{j.title}</div>
                    {j.department && <div className="text-xs text-slate-500">{j.department}</div>}
                    {j.archived && <span className="mt-1 inline-block text-[10px] uppercase tracking-wider text-slate-500">Archived</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-700 align-top">{j.location} <span className="text-slate-400">· {humanize(j.work_arrangement)}</span></td>
                  <td className="px-4 py-3 text-slate-700 align-top">{humanize(j.employment_type)}</td>
                  <td className="px-4 py-3 align-top">
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                      j.status === "published" ? "bg-green-50 text-green-700"
                      : j.status === "draft" ? "bg-amber-50 text-amber-700"
                      : "bg-slate-100 text-slate-600"
                    }`}>{humanize(j.status)}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-700 align-top" data-testid={`job-app-count-${j.slug}`}>{j.application_count ?? 0}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs align-top">{new Date(j.updated_at || j.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right align-top whitespace-nowrap">
                    <div className="inline-flex flex-wrap gap-2 justify-end">
                      {j.status === "published" && (
                        <a href={`/careers/${j.slug}`} target="_blank" rel="noreferrer" data-testid={`preview-${j.slug}`} className="text-slate-600 hover:text-[#4A6FA5] text-xs inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Preview</a>
                      )}
                      <Link to={`/care-portal/admin/jobs/${j.id}/edit`} data-testid={`edit-job-${j.slug}`} className="text-[#4A6FA5] hover:text-[#3B597B] text-xs font-medium">Edit</Link>
                      {j.status !== "published" && <button onClick={() => patchStatus(j.id, "published")} data-testid={`publish-${j.slug}`} className="text-green-700 hover:underline text-xs font-medium">Publish</button>}
                      {j.status === "published" && <button onClick={() => patchStatus(j.id, "closed")} data-testid={`close-${j.slug}`} className="text-amber-700 hover:underline text-xs font-medium">Close</button>}
                      {j.status === "closed" && <button onClick={() => patchStatus(j.id, "published")} data-testid={`reopen-${j.slug}`} className="text-green-700 hover:underline text-xs font-medium">Reopen</button>}
                      <button onClick={() => toggleArchive(j)} data-testid={`archive-${j.slug}`} className="text-slate-600 hover:underline text-xs font-medium">{j.archived ? "Unarchive" : "Archive"}</button>
                      <button onClick={() => setConfirmDelete(j)} data-testid={`delete-btn-${j.slug}`} className="text-red-600 hover:underline text-xs font-medium"><Trash2 className="w-3 h-3 inline" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>)}
      </div>

      {confirmDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 p-4" data-testid="job-delete-modal" onClick={() => setConfirmDelete(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-red-50 text-red-600 shrink-0"><AlertTriangle className="w-5 h-5" /></span>
              <div>
                <h3 className="font-heading text-lg font-semibold text-slate-900">Delete “{confirmDelete.title}”?</h3>
                <p className="mt-1 text-sm text-slate-600">Deleting is only allowed when the posting has no applications. Otherwise, archive it to preserve history.</p>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50">Cancel</button>
              <button onClick={doDelete} data-testid="confirm-delete-job" className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 inline-flex items-center gap-2"><Trash2 className="w-4 h-4" /> Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
